// Shared edge session policy (issue #1100).
//
// This is the single source of truth for the session numbers on the client,
// consumed identically by client/web-app and client/editor-korra via the
// "@korra/session" path alias. It mirrors the server-side constants in
// server-go/common/edgesession/edgesession.go — the SERVER is authoritative for
// expiry and revocation; these values only drive the client UX (idle countdown,
// proactive refresh, warning banner) so the client acts slightly ahead of the
// server rather than waiting to be rejected.
export const SESSION_POLICY = {
  /** Idle timeout: log out after this long with no user activity. */
  IDLE_MS: 30 * 60 * 1000,
  /** Absolute lifetime from login. A hard ceiling; renewal never crosses it. */
  ABSOLUTE_MS: 12 * 60 * 60 * 1000,
  /** Access-token lifetime. The token is proactively refreshed before this. */
  ACCESS_TOKEN_MS: 60 * 60 * 1000,
  /** Show the "you're about to be signed out" warning this long before idle logout. */
  WARN_BEFORE_MS: 60 * 1000,
  /** Refresh the access token this long before its exp, to avoid a 401 gap. */
  REFRESH_SKEW_MS: 5 * 60 * 1000,
  /**
   * Upper bound on the refresh cadence. MUST stay below IDLE_MS: the server only
   * advances last_activity on a refresh (edgesession.Touch), so if the first
   * refresh landed at exp-skew (~55m) it would exceed the 30m idle window and the
   * server would (wrongly) reject an active user as idle. Refreshing at least this
   * often keeps the server's last_activity fresh within the idle window (#1100).
   */
  REFRESH_INTERVAL_MS: 20 * 60 * 1000,
  /** Throttle for activity events, so a busy mouse doesn't reset timers 1000x/s. */
  ACTIVITY_THROTTLE_MS: 5 * 1000,
} as const;

// Standardized logout reasons, shared with the server (which returns them in the
// X-Logout-Reason header and refresh JSON body).
export type LogoutReason =
  "idle" | "absolute" | "token-expired" | "manual" | "revoked";

/** Response header the server stamps with a LogoutReason on session rejection. */
export const LOGOUT_REASON_HEADER = "x-logout-reason";

/** Query param the clients carry to the login page so it can explain the logout. */
export const LOGOUT_REASON_PARAM = "reason";

/**
 * Marks a logout that carries no reason (a forced sign-out after a failed
 * refresh, or one that ran because something failed). The login page holds its
 * automatic sign-in while it, or a reason, is present: an external IdP that
 * still has a session would otherwise sign the previous user straight back in.
 */
export const LOGGED_OUT_PARAM = "loggedOut";

/** Human-readable copy for each reason, for warning/redirect UX. */
export const LOGOUT_REASON_MESSAGE: Record<LogoutReason, string> = {
  idle: "You were signed out after 30 minutes of inactivity.",
  absolute: "Your session reached its 12-hour limit. Please sign in again.",
  "token-expired": "Your session expired. Please sign in again.",
  manual: "You have been signed out.",
  revoked: "Your session was ended. Please sign in again.",
};

export function isLogoutReason(value: unknown): value is LogoutReason {
  return (
    value === "idle" ||
    value === "absolute" ||
    value === "token-expired" ||
    value === "manual" ||
    value === "revoked"
  );
}
